import * as React from "react"
import { cn } from "@/lib/utils"

/**
 * IconButton — circular button that holds a single icon (no label).
 *
 * Usage:
 *   <IconButton aria-label="Search">
 *     <Search size={18} />
 *   </IconButton>
 *
 * Variants
 * ────────
 *  ghost    No border, white/60 → white on hover.  Nav, cards.  (default)
 *  outline  White/20 border, transparent bg.       Close buttons on dark backgrounds.
 *  primary  bg-sossiety-red, white border.          Edit photo, destructive actions.
 *
 * Sizes
 * ─────
 *  sm   w-8  h-8   — tight spaces, notification badges
 *  md   w-9  h-9   — default, top-nav, cards
 *  lg   w-10 h-10  — prominent standalone actions
 */

type IconButtonVariant = "ghost" | "outline" | "primary"
type IconButtonSize    = "sm" | "md" | "lg"

interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required for accessibility — describe the action, not the icon. */
  "aria-label": string
  variant?: IconButtonVariant
  size?: IconButtonSize
}

const variantMap: Record<IconButtonVariant, string> = {
  ghost:   "text-white/60 hover:text-white",
  outline: "border border-white/20 text-white/60 hover:border-white/40 hover:text-white",
  primary: "bg-sossiety-red text-white border-2 border-white hover:bg-sossiety-red-hover",
}

const sizeMap: Record<IconButtonSize, string> = {
  sm: "w-8  h-8",
  md: "w-9  h-9",
  lg: "w-10 h-10",
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, children, variant = "ghost", size = "md", ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        // Layout
        "inline-flex items-center justify-center rounded-full",
        // Interaction
        "transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sossiety-red/50 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent",
        "disabled:pointer-events-none disabled:opacity-50",
        // SVG pass-through
        "[&_svg]:pointer-events-none [&_svg]:shrink-0",
        // Variant + size
        variantMap[variant],
        sizeMap[size],
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
)
IconButton.displayName = "IconButton"

export { IconButton }
export type { IconButtonProps, IconButtonVariant, IconButtonSize }
